import React, { useEffect, useMemo } from "react";
import Select from "react-dropdown-select";
import { IdentityKycInputsProps, KycActivityKind } from "@/screen/Identities/IdentityKycInputs/Interfaces/IdentityKycInputsInterfaces";
import { PendingFieldShell } from "@/screen/Identities/Styles/IdentityFormStyles";
import { FormSection, FormRow, FormField, FormLabel, FormInput, FormSelect, QuestionText, RadioGroup, RadioLabel, RadioInput, DisclaimerBox, SearchableSelectWrapper, ActivityCard, ActivityCardHeader, ActivityRemoveButton, ActivityAddButtonRow, ActivityAddButton, InlineFieldError, SubQuestionIndent, PendingActivitiesHint } from "@/screen/Identities/IdentityKycInputs/Styles/IdentityKycInputsStyles";
import { getKycLocalBlocks, getKycServerErrorsByField, hasDuplicatedEmployedActivities, hasDuplicatedSelfEmployedActivities, isEmployedRowComplete, isSelfEmployedRowComplete } from "@/screen/Identities/IdentityForm/identityKycRules";
import type { KycCatalogItemDto, ProfessionCatalogItemDto } from "@/services/UserIdentitiesService";

const INTERNATIONAL_ACTIVITY_QUESTION_TEXT =
    "¿Realiza o tiene previsto realizar actividad comercial internacional (importación/exportación, " +
    "cobros o pagos con el exterior)?";

const RISK_COUNTRIES_QUESTION_TEXT =
    "¿Opera o mantiene relaciones económicas con países considerados de riesgo según la normativa " +
    "de prevención de blanqueo de capitales?";

const RISK_JURISDICTION_QUESTION_TEXT =
    "¿Realiza alguna actividad en jurisdicciones consideradas de riesgo o no cooperantes?";

const PEP_RELATIVE_QUESTION_TEXT =
    "¿Es familiar o allegado de una persona políticamente expuesta (PEP)?";

const OTHER_NATIONALITIES_QUESTION_TEXT =
    "¿Tiene alguna otra nacionalidad además de la indicada en sus datos personales?";

const EMPLOYED_GATE_QUESTION_TEXT =
    "¿Realiza actualmente o ha realizado alguna actividad económica por cuenta ajena?";

const SELF_EMPLOYED_GATE_QUESTION_TEXT =
    "¿Realiza actualmente o ha realizado alguna actividad económica por cuenta propia?";

const DUPLICATED_ACTIVITIES_TEXT =
    "Hay dos actividades idénticas en todas sus opciones. Elimina o modifica una de ellas para continuar.";

const MISSING_ACTIVITY_ROWS_TEXT =
    "Añade al menos una actividad y rellena todos sus datos para poder continuar.";

interface CodeSelectOption
{
    value: string;
    label: string;
}

function toProfessionOptions(items: ProfessionCatalogItemDto[] | null | undefined): CodeSelectOption[]
{
    return (items ?? []).map((item) => ({value: item.code, label: `${item.code} - ${item.description}`}));
}

function numberInputValue(value: number | null | undefined): string
{
    if(value === null || value === undefined)
    {
        return "";
    }
    return String(value);
}

/** Pregunta booleana Sí/No con el patrón de radios del formulario. */
const KycBooleanQuestion: React.FC<{name: string; question: string; value: boolean | undefined; pendingHighlight: boolean; disabled: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void}> = ({name, question, value, pendingHighlight, disabled, onChange}) =>
{
    return(
        <React.Fragment>
            <QuestionText>{question}</QuestionText>
            <PendingFieldShell $pendingHighlight={pendingHighlight}>
                <RadioGroup>
                    <RadioLabel>
                        <RadioInput type="radio" name={name} value="true" disabled={disabled}
                            onChange={onChange} checked={value === true}
                        />
                        Sí
                    </RadioLabel>
                    <RadioLabel>
                        <RadioInput type="radio" name={name} value="false" disabled={disabled}
                            onChange={onChange} checked={value === false}
                        />
                        No
                    </RadioLabel>
                </RadioGroup>
            </PendingFieldShell>
        </React.Fragment>
    );
};

/** Mensajes del servidor anclados bajo un campo. */
const FieldServerErrors: React.FC<{messages: string[] | undefined}> = ({messages}) =>
{
    if(!messages || messages.length === 0)
    {
        return null;
    }
    return(
        <React.Fragment>
            {messages.map((message, index) =>
            (
                <InlineFieldError key={index} role="alert">{message}</InlineFieldError>
            ))}
        </React.Fragment>
    );
};

const IdentityKycInputs: React.FC<IdentityKycInputsProps> = ({formData, validation, highlightPendingManualFields, isFormLocked, onChange, onActivityAdd, onActivityRemove, onActivityChange, onKycBlocked, identityCatalogs, catalogsLoading, professionCatalogsError, serverErrors}) =>
{
    const ph = (ok: boolean): boolean => highlightPendingManualFields && !ok;
    const selectDisabled = isFormLocked || catalogsLoading;

    const annualGrossIncomeOptions = identityCatalogs?.AnnualGrossIncomes ?? [];
    const fundsOriginOptions = identityCatalogs?.FundsOrigins ?? [];
    const personalCircumstancesOptions = identityCatalogs?.PersonalCircumstances ?? [];
    const activityStatusOptions = identityCatalogs?.EconomicActivityStatuses ?? [];
    const relatedProfessionalActivityOptions = identityCatalogs?.RelatedProfessionalActivities ?? [];
    const allowedDocumentation = identityCatalogs?.AllowedFundsDocumentationByOrigin ?? null;

    // La documentación acreditativa depende del origen de fondos (matriz del backend).
    const fundsDocumentationOptions = useMemo<KycCatalogItemDto[]>(() =>
    {
        const allTypes = identityCatalogs?.FundsDocumentationTypes ?? [];
        if(!formData.fundsOrigin || !allowedDocumentation)
        {
            return allTypes;
        }
        const allowedCodes = allowedDocumentation[formData.fundsOrigin] ?? [];
        return allTypes.filter((item) => allowedCodes.includes(item.code));
    }, [identityCatalogs?.FundsDocumentationTypes, allowedDocumentation, formData.fundsOrigin]);

    const cnoSelectOptions = useMemo<CodeSelectOption[]>(() => toProfessionOptions(identityCatalogs?.Cnos), [identityCatalogs?.Cnos]);
    const cnaeSelectOptions = useMemo<CodeSelectOption[]>(() => toProfessionOptions(identityCatalogs?.Cnaes), [identityCatalogs?.Cnaes]);

    // Bloqueos de alta anticipados en cliente (la decisión final es del servidor: D1).
    const localBlocks = useMemo(() => getKycLocalBlocks(formData), [formData]);
    const isKycBlocked = localBlocks.length > 0;
    const localBlockMessageByCode = useMemo(() =>
    {
        const byCode: Record<string, string> = {};
        for(const block of localBlocks)
        {
            byCode[block.code] = block.message;
        }
        return byCode;
    }, [localBlocks]);

    useEffect(() =>
    {
        onKycBlocked(isKycBlocked);
    }, [isKycBlocked, onKycBlocked]);

    // Errores KYC_* del servidor anclados por campo (bloqueos por nacionalidad, requeridos, coherencia...).
    const serverErrorsByField = useMemo(() => getKycServerErrorsByField(serverErrors), [serverErrors]);

    const renderCodeSelect = (name: string, value: string | null, options: KycCatalogItemDto[], pendingOk: boolean, disabled?: boolean): JSX.Element =>
    (
        <FormSelect name={name} value={value ?? ""} onChange={onChange} disabled={disabled ?? selectDisabled} $pendingHighlight={ph(pendingOk)}>
            <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
            {options.map((opt) =>
            (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
        </FormSelect>
    );

    const renderActivityRows = (kind: KycActivityKind): JSX.Element =>
    {
        const isEmployed = kind === "employed";
        const rows = isEmployed ? formData.employedActivities : formData.selfEmployedActivities;
        const professionOptions = isEmployed ? cnoSelectOptions : cnaeSelectOptions;
        const professionField = isEmployed ? "cnoCode" : "cnaeCode";
        const professionLabel = isEmployed ? "Profesión (CNO)" : "Actividad (CNAE)";
        const rowTitle = isEmployed ? "Actividad por cuenta ajena" : "Actividad por cuenta propia";
        // No se puede añadir otra fila mientras alguna esté incompleta (evita además duplicados de filas vacías).
        const hasIncompleteRows = isEmployed ?
            formData.employedActivities.some((r) => !isEmployedRowComplete(r)) :
            formData.selfEmployedActivities.some((r) => !isSelfEmployedRowComplete(r));

        return(
            <React.Fragment>
                {rows.map((row, index) =>
                {
                    const selectedProfessionCode = isEmployed ? formData.employedActivities[index].cnoCode : formData.selfEmployedActivities[index].cnaeCode;
                    const selectedProfession = professionOptions.filter((opt) => opt.value === selectedProfessionCode);
                    return(
                        <ActivityCard key={`${kind}-${index}`}>
                            <ActivityCardHeader>
                                <span>{rowTitle} {index + 1}</span>
                                <ActivityRemoveButton type="button" disabled={isFormLocked} onClick={() => onActivityRemove(kind, index)}>
                                    Eliminar
                                </ActivityRemoveButton>
                            </ActivityCardHeader>
                            <FormRow>
                                <FormField>
                                    <FormLabel required>Estado de la actividad</FormLabel>
                                    <FormSelect name={`${kind}-activityStatus-${index}`} value={row.activityStatus ?? ""} disabled={selectDisabled}
                                        onChange={(e) => onActivityChange(kind, index, "activityStatus", e.target.value === "" ? null : e.target.value)}
                                        $pendingHighlight={ph(!!row.activityStatus)}
                                    >
                                        <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                                        {activityStatusOptions.map((opt) =>
                                        (
                                            <option key={opt.code} value={opt.code}>{opt.label}</option>
                                        ))}
                                    </FormSelect>
                                </FormField>
                                <FormField>
                                    <FormLabel required>{professionLabel}</FormLabel>
                                    <SearchableSelectWrapper $pendingHighlight={ph(!!selectedProfessionCode)} $disabled={selectDisabled}>
                                        <Select<CodeSelectOption>
                                            options={professionOptions}
                                            values={selectedProfession}
                                            onChange={(selected) => onActivityChange(kind, index, professionField, selected[0]?.value ?? null)}
                                            placeholder={catalogsLoading ? "Cargando…" : "-- Seleccionar --"}
                                            searchable
                                            clearable={false}
                                            dropdownPosition="bottom"
                                            dropdownHeight="260px"
                                            labelField="label"
                                            valueField="value"
                                            searchBy="label"
                                            disabled={selectDisabled}
                                            name={`${kind}-${professionField}-${index}`}
                                            noDataLabel="No hay resultados"
                                        />
                                    </SearchableSelectWrapper>
                                    {professionCatalogsError &&
                                    (
                                        <InlineFieldError role="alert">{professionCatalogsError}</InlineFieldError>
                                    )}
                                </FormField>
                                <FormField>
                                    <FormLabel required>Años de ejercicio</FormLabel>
                                    <FormInput type="number" min={0} step={1} name={`${kind}-years-${index}`} disabled={isFormLocked} autoComplete="off"
                                        value={numberInputValue(row.years)}
                                        onChange={(e) =>
                                        {
                                            // Coherencia lógica: solo enteros no negativos (los negativos se llevan a 0).
                                            const parsed = Number(e.target.value);
                                            onActivityChange(kind, index, "years", e.target.value === "" || Number.isNaN(parsed) ? null : Math.max(0, Math.trunc(parsed)));
                                        }}
                                        $pendingHighlight={ph(row.years !== null && row.years !== undefined && row.years >= 0)}
                                    />
                                </FormField>
                            </FormRow>
                            {isEmployed &&
                            (
                                <FormRow>
                                    <FormField>
                                        <FormLabel required>Empresa Actual</FormLabel>
                                        <FormInput type="text" name={`${kind}-employedCurrentCompany-${index}`} disabled={isFormLocked} autoComplete="off"
                                            value={formData.employedActivities[index].employedCurrentCompany ?? ""}
                                            onChange={(e) => onActivityChange(kind, index, "employedCurrentCompany", e.target.value === "" ? null : e.target.value)}
                                            $pendingHighlight={ph(!!formData.employedActivities[index].employedCurrentCompany)}
                                        />
                                    </FormField>
                                </FormRow>
                            )}
                            {!isEmployed &&
                            (
                                <FormRow>
                                    <FormField>
                                        <FormLabel required>Actividad profesional relacionada</FormLabel>
                                        <FormSelect name={`${kind}-relatedProfessionalActivity-${index}`} value={formData.selfEmployedActivities[index].relatedProfessionalActivity ?? ""} disabled={selectDisabled}
                                            onChange={(e) => onActivityChange(kind, index, "relatedProfessionalActivity", e.target.value === "" ? null : e.target.value)}
                                            $pendingHighlight={ph(!!formData.selfEmployedActivities[index].relatedProfessionalActivity)}
                                        >
                                            <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                                            {relatedProfessionalActivityOptions.map((opt) =>
                                            (
                                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                                            ))}
                                        </FormSelect>
                                    </FormField>
                                </FormRow>
                            )}
                        </ActivityCard>
                    );
                })}
                {/* La puerta contestada "Sí" sin ninguna fila también bloquea el botón,
                    pero no hay input que resaltar: avisamos con el mismo tono ámbar. */}
                {rows.length === 0 && highlightPendingManualFields &&
                (
                    <PendingFieldShell $pendingHighlight>
                        <PendingActivitiesHint>{MISSING_ACTIVITY_ROWS_TEXT}</PendingActivitiesHint>
                    </PendingFieldShell>
                )}
                <ActivityAddButtonRow>
                    <ActivityAddButton type="button" disabled={isFormLocked || hasIncompleteRows} onClick={() => onActivityAdd(kind)}
                        title={hasIncompleteRows ? "Completa la actividad actual antes de añadir otra." : undefined}
                    >
                        + Añadir actividad
                    </ActivityAddButton>
                </ActivityAddButtonRow>
            </React.Fragment>
        );
    };

    return(
        <FormSection>
            <h3>Conocimiento del Cliente (KYC)</h3>
            <FormRow>
                <FormField>
                    <FormLabel required>Ingresos brutos anuales</FormLabel>
                    {renderCodeSelect("annualGrossIncome", formData.annualGrossIncome, annualGrossIncomeOptions, validation.annualGrossIncome)}
                    <FieldServerErrors messages={serverErrorsByField.annualGrossIncome} />
                </FormField>
                <FormField>
                    <FormLabel required>Número de personas a su cargo</FormLabel>
                    <FormInput type="number" min={0} step={1} name="numberOfDependants" disabled={isFormLocked} autoComplete="off"
                        value={numberInputValue(formData.numberOfDependants)} onChange={onChange}
                        $pendingHighlight={ph(validation.numberOfDependants)}
                    />
                    <FieldServerErrors messages={serverErrorsByField.numberOfDependants} />
                </FormField>
                <FormField>
                    <FormLabel required>Otras circunstancias personales</FormLabel>
                    {renderCodeSelect("otherPersonalCircumstances", formData.otherPersonalCircumstances, personalCircumstancesOptions, validation.otherPersonalCircumstances)}
                    <FieldServerErrors messages={serverErrorsByField.otherPersonalCircumstances} />
                </FormField>
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel required>Origen de los fondos</FormLabel>
                    {renderCodeSelect("fundsOrigin", formData.fundsOrigin, fundsOriginOptions, validation.fundsOrigin)}
                    <FieldServerErrors messages={serverErrorsByField.fundsOrigin} />
                </FormField>
                <FormField>
                    <FormLabel required>Documentación acreditativa del origen</FormLabel>
                    {renderCodeSelect("fundsDocumentationProvided", formData.fundsDocumentationProvided, fundsDocumentationOptions, validation.fundsDocumentationProvided, selectDisabled || !formData.fundsOrigin)}
                    <FieldServerErrors messages={serverErrorsByField.fundsDocumentationProvided} />
                </FormField>
                <FormField>
                    <FormLabel required>Ciudadano de EEUU</FormLabel>
                    <FormSelect name="isUsCitizen" value={formData.isUsCitizen === undefined ? "" : String(formData.isUsCitizen)} disabled={isFormLocked}
                        onChange={onChange} $pendingHighlight={ph(validation.isUsCitizen)}
                    >
                        <option value="">-- Seleccionar --</option>
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                    </FormSelect>
                    {localBlockMessageByCode.KYC_BLOCKED_US_CITIZEN &&
                    (
                        <DisclaimerBox>
                            <p>{localBlockMessageByCode.KYC_BLOCKED_US_CITIZEN}</p>
                        </DisclaimerBox>
                    )}
                </FormField>
            </FormRow>
            {/* ─── Preguntas booleanas (posibles bloqueos de alta) ─── */}
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="internationalCommercialActivity" question={INTERNATIONAL_ACTIVITY_QUESTION_TEXT}
                        value={formData.internationalCommercialActivity} pendingHighlight={ph(validation.internationalCommercialActivity)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    {localBlockMessageByCode.KYC_BLOCKED_INTERNATIONAL_ACTIVITY &&
                    (
                        <DisclaimerBox>
                            <p>{localBlockMessageByCode.KYC_BLOCKED_INTERNATIONAL_ACTIVITY}</p>
                        </DisclaimerBox>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.internationalCommercialActivity} />
                </FormField>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="riskCountries" question={RISK_COUNTRIES_QUESTION_TEXT}
                        value={formData.riskCountries} pendingHighlight={ph(validation.riskCountries)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    {localBlockMessageByCode.KYC_BLOCKED_RISK_COUNTRIES &&
                    (
                        <DisclaimerBox>
                            <p>{localBlockMessageByCode.KYC_BLOCKED_RISK_COUNTRIES}</p>
                        </DisclaimerBox>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.riskCountries} />
                </FormField>
            </FormRow>
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="riskJurisdictionActivity" question={RISK_JURISDICTION_QUESTION_TEXT}
                        value={formData.riskJurisdictionActivity} pendingHighlight={ph(validation.riskJurisdictionActivity)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    {localBlockMessageByCode.KYC_BLOCKED_RISK_JURISDICTION &&
                    (
                        <DisclaimerBox>
                            <p>{localBlockMessageByCode.KYC_BLOCKED_RISK_JURISDICTION}</p>
                        </DisclaimerBox>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.riskJurisdictionActivity} />
                </FormField>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="isPepRelative" question={PEP_RELATIVE_QUESTION_TEXT}
                        value={formData.isPepRelative} pendingHighlight={ph(validation.isPepRelative)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    {localBlockMessageByCode.KYC_BLOCKED_PEP_RELATIVE &&
                    (
                        <DisclaimerBox>
                            <p>{localBlockMessageByCode.KYC_BLOCKED_PEP_RELATIVE}</p>
                        </DisclaimerBox>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.isPepRelative} />
                </FormField>
            </FormRow>
            {/* La otra nacionalidad vive en Datos Fiscales (junto a las preguntas de EEUU); como esa
                sección no se pinta para menores, aquí solo se muestra en el alta/edición de un menor. */}
            {formData.isMinor &&
            (
                <FormRow>
                    <FormField style={{flex: "1"}}>
                        <KycBooleanQuestion name="otherNationalities" question={OTHER_NATIONALITIES_QUESTION_TEXT}
                            value={formData.otherNationalities} pendingHighlight={ph(validation.otherNationalities)}
                            disabled={isFormLocked} onChange={onChange}
                        />
                        <FieldServerErrors messages={serverErrorsByField.otherNationalities} />
                        {formData.otherNationalities === true &&
                        (
                            <SubQuestionIndent>
                                <FormLabel required>Indique la otra nacionalidad</FormLabel>
                                <FormInput type="text" name="otherNationality" maxLength={50} disabled={isFormLocked} autoComplete="off"
                                    value={formData.otherNationality} onChange={onChange}
                                    $pendingHighlight={ph(validation.otherNationality)}
                                />
                                <FieldServerErrors messages={serverErrorsByField.otherNationality} />
                            </SubQuestionIndent>
                        )}
                    </FormField>
                    <FormField style={{flex: "1"}} />
                </FormRow>
            )}
            {/* ─── Actividades económicas (al final: sus filas expanden la sección hacia abajo) ─── */}
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="hasEmployedActivity" question={EMPLOYED_GATE_QUESTION_TEXT}
                        value={formData.hasEmployedActivity} pendingHighlight={ph(validation.hasEmployedActivity)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    <FieldServerErrors messages={serverErrorsByField.hasEmployedActivity} />
                </FormField>
            </FormRow>
            {formData.hasEmployedActivity === true &&
            (
                <SubQuestionIndent>
                    {renderActivityRows("employed")}
                    {hasDuplicatedEmployedActivities(formData.employedActivities) &&
                    (
                        <InlineFieldError role="alert">{DUPLICATED_ACTIVITIES_TEXT}</InlineFieldError>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.employedActivities} />
                </SubQuestionIndent>
            )}
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <KycBooleanQuestion name="hasSelfEmployedActivity" question={SELF_EMPLOYED_GATE_QUESTION_TEXT}
                        value={formData.hasSelfEmployedActivity} pendingHighlight={ph(validation.hasSelfEmployedActivity)}
                        disabled={isFormLocked} onChange={onChange}
                    />
                    <FieldServerErrors messages={serverErrorsByField.hasSelfEmployedActivity} />
                </FormField>
            </FormRow>
            {formData.hasSelfEmployedActivity === true &&
            (
                <SubQuestionIndent>
                    {renderActivityRows("selfEmployed")}
                    {hasDuplicatedSelfEmployedActivities(formData.selfEmployedActivities) &&
                    (
                        <InlineFieldError role="alert">{DUPLICATED_ACTIVITIES_TEXT}</InlineFieldError>
                    )}
                    <FieldServerErrors messages={serverErrorsByField.selfEmployedActivities} />
                </SubQuestionIndent>
            )}
        </FormSection>
    );
};

export default IdentityKycInputs;
